import type {
    CreateExtArgs,
    DocumentDataTypes,
    Extension,
    ExtensionAPI,
    InitializeExtArgs
} from '@wix/document-manager-core'
import type {AppsInstallStateExtensionAPI} from './appsInstallState'
import type {AppDefinitionId, AppStateChangedCallback, AppStateManifestListener} from '@wix/document-services-types'
import type {DistributorExtensionAPI} from './distributor/distributor'
import type {OpenPlatformPanel, PlatformSharedAPIState, PlatformSharedManifestState} from './distributor/messages'

const PANEL_STATE = {
    OPEN: 'open',
    CLOSED: 'closed'
}

type PlatformAPICallsListener = (appDefId: string) => void

const getDocumentDataTypes = (): DocumentDataTypes => ({})

const EVENTS = {
    CONTEXT: {
        API_CALLED: 'API_CALLED'
    }
}

export interface PlatformSharedState extends ExtensionAPI {
    platformSharedState: {
        notifyManifestWasChanged(appDefId: string): void
        subscribeToManifestWasChanges(listener: AppStateManifestListener): void
        notifyPlatformAPIWasCalled(appDefId: string): void
        subscribeToPlatformAPICalls(appDefId: string, listener: PlatformAPICallsListener): void
        unsubscribeToPlatformAPICalls(appDefId: string): void
        subscribeToPendingAppsChanges(listener: AppStateChangedCallback): void
    }
}

export interface PlatformSharedStatePublicAPI extends ExtensionAPI {
    platformSharedState: {
        subscribeToPendingAppsChanges: PlatformSharedState['platformSharedState']['subscribeToPendingAppsChanges']
        subscribeToManifestChange: PlatformSharedState['platformSharedState']['subscribeToManifestWasChanges']
    }
}

const createExtension = (): Extension => {
    const openPlatformPanelsMap = new Map()

    const platformAPICallsListeners: Record<string, PlatformAPICallsListener> = {}

    const notifyAPIChangeListeners = (appDefIds: string[]) => {
        appDefIds.forEach(appDefId => {
            try {
                if (platformAPICallsListeners[appDefId]) {
                    platformAPICallsListeners[appDefId](appDefId)
                }
            } catch (e) {
                console.error(e)
            }
        })
    }

    const updateOpenPanelsMap = (appDefinitionId: AppDefinitionId, action: string) => {
        try {
            if (action === PANEL_STATE.OPEN) {
                if (!openPlatformPanelsMap.get(appDefinitionId)) {
                    openPlatformPanelsMap.set(appDefinitionId, {count: 0})
                }
                const openPanelObjForApp = openPlatformPanelsMap.get(appDefinitionId)
                openPanelObjForApp.count += 1
                openPlatformPanelsMap.set(appDefinitionId, openPanelObjForApp)
            } else {
                const openPanelObjForApp = openPlatformPanelsMap.get(appDefinitionId)
                openPanelObjForApp.count -= 1
                openPlatformPanelsMap.set(appDefinitionId, openPanelObjForApp)
                if (openPanelObjForApp.count <= 0) {
                    openPlatformPanelsMap.delete(appDefinitionId)
                }
            }
        } catch (e) {
            console.error(e)
        }
    }

    const notifyPlatformAPIWasCalledInternal = (extensionAPI: ExtensionAPI, appDefinitionId: string) => {
        const {distributor} = extensionAPI as DistributorExtensionAPI
        distributor.distributeMessageAfterApproval<PlatformSharedAPIState>('platformSharedAPIState', {appDefinitionId})
    }

    const notifyPlatformPanelStateChanged = (extensionAPI: ExtensionAPI, appDefinitionId: string, state: string) => {
        const {distributor} = extensionAPI as DistributorExtensionAPI
        distributor.distributeMessageAfterApproval<OpenPlatformPanel>('openPlatformPanelsState', {
            appDefinitionId,
            action: state
        })
    }

    const initialState = {}

    const createExtensionAPI = ({extensionAPI}: CreateExtArgs): PlatformSharedState => ({
        platformSharedState: {
            notifyManifestWasChanged: (appDefinitionId: string) => {
                const {distributor} = extensionAPI as DistributorExtensionAPI
                distributor.distributeMessage<PlatformSharedManifestState>('platformSharedManifestState', {
                    appDefinitionId
                })
            },
            subscribeToManifestWasChanges: (listener: AppStateManifestListener) => {
                const {distributor} = extensionAPI as DistributorExtensionAPI
                distributor.subscribeToMessage<PlatformSharedManifestState>(
                    'platformSharedManifestState',
                    ({data: {appDefinitionId}}) => listener(appDefinitionId)
                )
            },
            notifyPlatformAPIWasCalled: appDefId => notifyPlatformAPIWasCalledInternal(extensionAPI, appDefId),
            subscribeToPlatformAPICalls: (appDefinitionId: string, listener: PlatformAPICallsListener) => {
                platformAPICallsListeners[appDefinitionId] = listener
                notifyPlatformPanelStateChanged(extensionAPI, appDefinitionId, PANEL_STATE.OPEN)
            },
            unsubscribeToPlatformAPICalls: (appDefinitionId: string) => {
                notifyPlatformPanelStateChanged(extensionAPI, appDefinitionId, PANEL_STATE.CLOSED)
                delete platformAPICallsListeners[appDefinitionId]
            },
            subscribeToPendingAppsChanges: (listener: AppStateChangedCallback) => {
                const {appsInstallState} = extensionAPI as AppsInstallStateExtensionAPI
                appsInstallState.subscribeToStateChanges(listener)
            }
        }
    })

    const createPublicAPI = (args: CreateExtArgs): PlatformSharedStatePublicAPI => {
        const {platformSharedState} = args.extensionAPI as PlatformSharedState

        return {
            platformSharedState: {
                subscribeToPendingAppsChanges: platformSharedState.subscribeToPendingAppsChanges,
                subscribeToManifestChange: platformSharedState.subscribeToManifestWasChanges
            }
        }
    }

    const initialize = async ({eventEmitter, extensionAPI}: InitializeExtArgs) => {
        const {distributor} = extensionAPI as DistributorExtensionAPI
        distributor.subscribeToMessage<PlatformSharedAPIState>('platformSharedAPIState', ({data: {appDefinitionId}}) =>
            notifyAPIChangeListeners([appDefinitionId])
        )
        distributor.subscribeToMessage<OpenPlatformPanel>(
            'openPlatformPanelsState',
            ({data: {appDefinitionId, action}}) => updateOpenPanelsMap(appDefinitionId, action)
        )
        eventEmitter.on(EVENTS.CONTEXT.API_CALLED, (appDefId: string) => {
            const otherEditorHaveAppPanelOpened = openPlatformPanelsMap.get(appDefId)
            if (otherEditorHaveAppPanelOpened && otherEditorHaveAppPanelOpened.count > 0) {
                notifyPlatformAPIWasCalledInternal(extensionAPI, appDefId)
            }
        })
    }

    return {
        name: 'platformSharedState',
        getDocumentDataTypes,
        initialState,
        createExtensionAPI,
        createPublicAPI,
        initialize,
        EVENTS
    }
}

export {createExtension, EVENTS}
